"use client";

import type { NodeViewProps } from "@tiptap/react";

import { useNodeViewSelected } from "@/components/ui/editor/lib/use-node-view-selected";

export interface InlineNodeDecoration {
  /** The chip is part of the current selection (node- or text-selection). */
  isSelected: boolean;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isLink: boolean;
  linkHref?: string;
}

/**
 * Shared selection + mark state for inline atom chips (variable mentions and
 * AI-prompt blocks). Both render as inline atoms, so they support the same
 * formatting marks (bold / italic / underline / link) and should show a blue
 * outline when they're inside the current selection — mirroring exactly what
 * the backend renderer wraps them in (`<strong>/<em>/<u>/<a>`).
 */
export function useInlineNodeDecoration({
  node,
  editor,
  getPos,
  selected,
}: Pick<
  NodeViewProps,
  "node" | "editor" | "getPos" | "selected"
>): InlineNodeDecoration {
  const isSelected = useNodeViewSelected({ node, editor, getPos, selected });

  const marks = node.marks ?? [];
  const markNames = new Set(marks.map((mark) => mark.type.name));
  const linkMark = marks.find((mark) => mark.type.name === "link");

  return {
    isSelected,
    isBold: markNames.has("bold"),
    isItalic: markNames.has("italic"),
    isUnderline: markNames.has("underline"),
    isLink: markNames.has("link"),
    linkHref:
      typeof linkMark?.attrs?.href === "string"
        ? (linkMark.attrs.href as string)
        : undefined,
  };
}
